import React, { Component } from 'react';
import dateFormat from 'dateformat';

import { buildPaginationQuery, getPaginatedData } from '../utils/pagination';
import { matchesSearchTerms } from '../utils/searchTerms';
import { TICKET_PAYMENT_METHOD } from '../utils/enums';
import { formatEventDateTime } from '../utils/formats';
import { printAdmissionsList } from '../utils/printAdmissions';
import { startActivity, textoDeAviso, drawTables, assignTable } from '../utils/activitiesApi';
import { hasInclusions } from '../utils/eventIncluded';
import ModalPrintAdmissionsComponent from './ModalPrintAdmissionsComponent';
import { copyText } from '../utils/publicLinks';

var config = process.env;

// ENI-213: "redimir" es jerga de ticketing; lo que hace el operador es el check-in de la
// persona. Y "Válida"/"Pendiente" no se distinguían: `pending` significa OTRA cosa (pago
// online sin confirmar), no "todavía no entró".
const ADMISSION_STATUS_LABEL = {
    valid: 'Sin ingresar',
    redeemed: 'Ingresó',
    cancelled: 'Cancelada',
    pending: 'Pago pendiente',
    unpaid: 'Sin ingresar',
    refund_pending: 'Reembolso pendiente',
    refunded: 'Reembolsada',
};

// Estado del COBRO, que es un eje distinto del check-in y hasta ahora no se veía: la columna
// "Estado" mezclaba los dos, así que una vez que la persona ingresaba no quedaba rastro de si
// había pagado, ni con qué. Son ortogonales — se puede estar sin ingresar y pagado, o impago.
const PAYMENT_LABEL_BY_METHOD = {
    efectivo: TICKET_PAYMENT_METHOD[1],
    transferencia: TICKET_PAYMENT_METHOD[2],
    mercadopago: TICKET_PAYMENT_METHOD[3],
    pos: TICKET_PAYMENT_METHOD[5],
};

// Opciones del filtro de estado. Viven en una constante y no sueltas en el JSX porque el
// listado impreso rotula en el encabezado QUÉ filtro se imprimió: con las etiquetas sólo en
// el <option>, el papel y la pantalla podrían terminar diciendo cosas distintas.
const STATUS_FILTER_OPTIONS = [
    { value: 'all', label: 'Todos' },
    { value: 'valid', label: 'Sin ingresar' },
    // Lo que hay que cobrar. Es la lista que el mostrador necesita tener a mano cuando la
    // gente llega a la puerta.
    { value: 'unpaid', label: 'Impagas' },
    { value: 'pending', label: 'Pago pendiente' },
    { value: 'redeemed', label: 'Ingresó' },
    { value: 'cancelled', label: 'Cancelada' },
    { value: 'refund_pending', label: 'Reembolso pendiente' },
    // Faltaba: con el filtro abriendo en "Todos" estas filas se ven al entrar, así que tiene
    // que existir la forma de aislarlas.
    { value: 'refunded', label: 'Reembolsada' },
];

// Métodos con los que se puede cobrar una entrada que quedó impaga (espeja PRESENCIAL_METHODS).
const COBRO_METHODS = [
    { value: 'efectivo', label: TICKET_PAYMENT_METHOD[1] },
    { value: 'transferencia', label: TICKET_PAYMENT_METHOD[2] },
    { value: 'pos', label: TICKET_PAYMENT_METHOD[5] },
    { value: 'mercadopago', label: TICKET_PAYMENT_METHOD[3] },
];

// Entradas que se sortean o se cambian de mesa: las vivas que todavía no ingresaron.
const SORTEABLES = ['valid', 'unpaid', 'pending'];

class TableEventAdmissionsComponent extends Component {

    constructor() {
        super();
        this.state = {
            // ENI-227: abre en "Todos". El default vive ACÁ y no en el backend: el front manda
            // siempre ?status=<este valor>, así que el default de listAdmissions nunca se
            // ejercita desde la UI y tocarlo allá no hubiera cambiado nada en pantalla.
            // "Sin ingresar" (la lista de check-in en puerta) queda a un click.
            statusFilter: 'all',
            // El buscador filtra en el cliente a propósito: el endpoint trae el evento entero
            // sin paginar, y en la puerta —con la fila esperando— el operador necesita que la
            // lista responda mientras tipea, no un viaje al servidor por tecla.
            search: '',
            data: [],
            // El evento viene en la MISMA respuesta que las admisiones; se guarda para poder
            // encabezar el listado impreso con su nombre y fecha.
            event: null,
            loaded: false,
            disabled: false,
            // Método elegido para cobrar cada entrada impaga, por id de admisión (default efectivo).
            cobroMethod: {},
            // FR-034: qué pasó con las mesas del salón en el último check-in. No bloquea nada
            // (la entrada YA ingresó); es el aviso de que las mesas quedaron sin sentar.
            activityNotice: null,
        };
    }

    componentDidMount() {
        this.fetchData();
    }

    // Expuesto para que la página recargue tras una venta presencial.
    refresh = () => {
        this.fetchData({ status: this.state.statusFilter });
    }

    fetchData = ({ status = this.state.statusFilter } = {}) => {
        this.setState({ loaded: false });
        const request = {
            method: 'GET',
            headers: {
                'access-token': localStorage.getItem('token'),
                'Content-Type': 'application/json',
            },
        };
        const query = buildPaginationQuery({ extraParams: { status: status || undefined } });
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}manage-events/${this.props.eventId}/admissions?${query}`, request)
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) {
                    // ENI-210: idem EditEventPage — el motivo puntual va en data.data.message.
                    alert((data.data && data.data.message) || data.message);
                    if (data.error == 1002) {
                        window.location.href = localStorage.getItem('homePageURL');
                    }
                } else {
                    // El backend devuelve { data: { event, admissions: [...] } } (snake_case).
                    const admissions = (data.data && data.data.admissions) || [];
                    const event = (data.data && data.data.event) || this.state.event;
                    this.setState({ data: admissions, event, statusFilter: status, loaded: true });
                }
            })
            .catch((error) => {
                console.error('No se pudieron cargar los asistentes:', error);
                this.setState({ data: [], statusFilter: status, loaded: true });
            });
    }

    componentWillUnmount() {
        clearTimeout(this.recienTimer);
    }

    onChangeStatus = (e) => {
        this.fetchData({ status: e.target.value });
    }

    onChangeSearch = (e) => {
        this.setState({ search: e.target.value });
    }

    // Se busca sobre las columnas que la grilla MUESTRA (nombre, mail, celular, tipo de entrada
    // y la mesa de la jornada). Matchear un campo invisible devuelve filas donde no se ve por qué
    // entraron. La mesa sólo cuenta si la columna está en pantalla: sin mesas en la jornada,
    // "mesa" no tiene que traer a nadie por una asignación vieja que no se ve.
    visibleAdmissions = () => this.state.data.filter((item) => matchesSearchTerms([
        this.holderName(item),
        this.holderEmail(item),
        this.holderPhone(item),
        item.ticketType ? item.ticketType.name : '',
        this.mesas().length ? this.labelMesa(item.assigned_table_id) : '',
    ], this.state.search))

    // ── La trampa del día en un evento con consumo incluido ──
    //
    // Un evento que pactó cantidades se sienta de una, con «Sentar a todos». Pero un solo
    // «Marcar ingreso» sienta las mesas por su cuenta, la jornada pasa a «En mesa» y ese botón
    // deja de existir: quedan los demás ingresos para marcar uno por uno. Mientras la jornada
    // siga en «Mesas reservadas» el ingreso suelto se pregunta antes, y «Sentar a todos» está
    // también acá arriba, que es donde están los botones de «Marcar ingreso».
    //
    // `included` es `event.included` (lo baja la página, igual que `activity`). Sin él —un evento
    // que no declara nada— todo queda como siempre.
    seSientaDeUna = () => {
        const act = (this.props || {}).activity;
        return hasInclusions((this.props || {}).included) && !!act && act.status === 'reserved';
    }

    // Cuántos se sentarían: los anotados del evento, el mismo número de la card de mesas.
    anotadosParaSentar = () => (this.seSientaDeUna() ? Number(this.props.activity.expected) || 0 : 0)

    marcarIngreso = (admissionId) => {
        if (this.seSientaDeUna() && !window.confirm('Este evento se sienta de una con «Sentar a todos». Si marcás este ingreso suelto, ese botón deja de estar. ¿Marcar igual?')) {
            return;
        }
        this.redeem(admissionId);
    }

    // «Sentar a todos»: lo mismo que el botón de la card «Mesas del salón». El backend sienta
    // las mesas, reparte parejo a los anotados y marca sus entradas como ingresadas; fuera del
    // día de la jornada lo rechaza con su motivo.
    sentarATodos = () => {
        const act = this.props.activity;
        if (!this.seSientaDeUna()) return;
        this.setState({ disabled: true, activityNotice: null });
        startActivity(act.id, { everyone: true })
            .then((data) => {
                const warnings = (data && data.warnings) || [];
                const sentadas = Number(data && data.seated) || 0;
                this.setState({
                    disabled: false,
                    activityNotice: {
                        ok: `Se sentaron ${sentadas} ${sentadas === 1 ? 'mesa' : 'mesas'} con todos los anotados.`,
                        message: null,
                        warnings,
                        activityId: null,
                    },
                });
                this.fetchData({ status: this.state.statusFilter });
                if (this.props.onActivityChange) this.props.onActivityChange();
            })
            .catch((e) => {
                this.setState({ disabled: false });
                alert(e.message);
            });
    }

    renderSentarATodos = () => {
        const anotados = this.anotadosParaSentar();
        if (!(anotados > 0)) return null;
        return (
            <div className="px-3 py-2 border-bottom d-flex flex-wrap align-items-center event-seat-everyone" style={{ gap: 8, fontSize: 13 }}>
                <span>Este evento se sienta de una, sin marcar los ingresos uno por uno:</span>
                <button type="button" className="btn btn-sm btn-accent" disabled={this.state.disabled} onClick={this.sentarATodos}>
                    Sentar a todos ({anotados})
                </button>
            </div>
        );
    }

    redeem = (admissionId) => {
        this.setState({ disabled: true, activityNotice: null });
        const request = {
            method: 'PATCH',
            headers: {
                'access-token': localStorage.getItem('token'),
                'Content-Type': 'application/json',
            },
        };
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}manage-events/${this.props.eventId}/admissions/${admissionId}/redeem`, request)
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) {
                    // ENI-116: mensaje de negocio anidado (INVALID_STATE, etc.) si viene.
                    alert((data.data && data.data.message) || data.message);
                    this.setState({ disabled: false });
                    if (data.error == 1002) {
                        window.location.href = localStorage.getItem('homePageURL');
                    }
                } else {
                    // FR-034: la respuesta trae `activity` con lo que pasó en el salón. La
                    // entrada ingresó igual — esto no es un error, es información que hasta
                    // ahora se descartaba entera.
                    const activity = (data.data && data.data.activity) || null;
                    this.setState({ disabled: false, activityNotice: this.avisoDeActividad(activity), recienIngreso: admissionId });
                    clearTimeout(this.recienTimer);
                    this.recienTimer = setTimeout(() => this.setState({ recienIngreso: null }), 2500);
                    this.fetchData({ status: this.state.statusFilter });
                    // Un check-in mueve DOS cosas de esta pantalla: la grilla de acá arriba y la
                    // card "Mesas del salón" de abajo, que muestra Anotados / Ingresaron del
                    // mismo evento. El refresco NO puede colgar de `activity.activityId`: sin
                    // actividad el backend contesta `activityId: null` (`checkInByEvent`, el
                    // caso del 99% de los eventos — NFR-007), y entonces la grilla mostraba la
                    // entrada ya ingresada mientras la card de arriba seguía diciendo el número
                    // viejo, dos datos contradiciéndose en la misma pantalla.
                    if (this.props.onActivityChange) this.props.onActivityChange();
                }
            })
            .catch((error) => {
                console.error('No se pudo redimir la entrada:', error);
                alert('Error de conexión');
                this.setState({ disabled: false });
            });
    }

    /**
     * Qué aviso corresponde para el `activity` de un check-in (FR-034), o `null`.
     *
     * DOS motivos distintos para avisar, y hay que quedarse con los dos:
     *
     *   - `status ∉ {seated, none}` — 'skipped' (la jornada es de otro día) o 'failed' (el
     *     salón se cayó): las mesas NO se sentaron y hay que sentarlas a mano.
     *   - `warnings` con algo adentro AUNQUE el status sea 'seated' — es S-04l: el claim sentó
     *     dos mesas y la tercera estaba ocupada por un walk-in. Gatear sólo por el status
     *     escondería justo el caso que más importa mostrarle al mostrador.
     */
    avisoDeActividad = (activity) => {
        if (!activity) return null;
        const warnings = activity.warnings || [];
        const bloqueado = activity.status !== 'seated' && activity.status !== 'none';
        if (!bloqueado && warnings.length === 0) return null;
        return {
            message: bloqueado ? (activity.message || 'las mesas no se sentaron') : null,
            warnings,
            activityId: activity.activityId || null,
        };
    }

    // "Sentar las mesas" desde el aviso: el mismo fallback de FR-036 que ofrece la card de la
    // jornada, a mano en la pantalla donde el mostrador se enteró del problema.
    //
    // Cuando sale bien TIENE que decirlo. Antes anulaba el aviso y el cartel amarillo se
    // esfumaba sin dejar nada: el mostrador apretaba el botón justamente porque el check-in le
    // avisó que las mesas no se habían sentado, y se quedaba sin saber si sentó tres, una o
    // ninguna. El número lo responde `seated`, que la respuesta trae (`ActivityController.start`
    // devuelve `{ activity, seated, warnings }`) y que hasta ahora se descartaba. Es lo mismo
    // que ya confirma la card de la jornada ("Se sentaron N mesas").
    sentarMesas = () => {
        const aviso = this.state.activityNotice;
        if (!aviso || !aviso.activityId) return;
        this.setState({ disabled: true });
        startActivity(aviso.activityId)
            .then((data) => {
                const warnings = (data && data.warnings) || [];
                const sentadas = Number(data && data.seated) || 0;
                this.setState({
                    disabled: false,
                    activityNotice: {
                        ok: `Se sentaron ${sentadas} ${sentadas === 1 ? 'mesa' : 'mesas'}.`,
                        message: null,
                        warnings,
                        // Con avisos quedó al menos una mesa sin sentar y el botón sigue a mano
                        // para reintentarla; sin avisos no hay nada que reintentar.
                        activityId: warnings.length ? aviso.activityId : null,
                    },
                });
                if (this.props.onActivityChange) this.props.onActivityChange();
            })
            .catch((e) => {
                this.setState({ disabled: false });
                alert(e.message);
            });
    }

    renderActivityNotice = () => {
        const aviso = this.state.activityNotice;
        if (!aviso) return null;
        const warnings = aviso.warnings || [];
        // Verde sólo cuando no quedó nada pendiente: con avisos alguna mesa sigue sin sentarse
        // y el cartel tiene que seguir llamando la atención.
        const exito = !!aviso.ok && !aviso.message && warnings.length === 0;
        return (
            <div className={`alert ${exito ? 'alert-success' : 'alert-warning'} m-3`} role="alert">
                {aviso.message && (
                    <span>La entrada ingresó pero las mesas no se sentaron: {aviso.message}. Usá "Sentar las mesas".</span>
                )}
                {!aviso.message && aviso.ok && <span>{aviso.ok}</span>}
                {!aviso.message && !aviso.ok && <span>La entrada ingresó y las mesas se sentaron, con avisos:</span>}
                {warnings.length > 0 && (
                    <>
                        {aviso.ok && <span> El salón avisa:</span>}
                        <ul className="mb-0 mt-1">
                            {warnings.map((w, i) => (
                                <li key={i}>{textoDeAviso(w)}</li>
                            ))}
                        </ul>
                    </>
                )}
                <div className="mt-2">
                    {aviso.activityId && (
                        <button type="button" className="btn btn-sm btn-accent mr-2" disabled={this.state.disabled} onClick={this.sentarMesas}>
                            Sentar las mesas
                        </button>
                    )}
                    <button type="button" className="btn btn-sm btn-white" onClick={() => this.setState({ activityNotice: null })}>
                        Entendido
                    </button>
                </div>
            </div>
        );
    }

    // ENI-163: anula/cancela una entrada individual (libera cupo). Distinta del check-in.
    // Una 'pending' (checkout de Mercado Pago abandonado) también se puede anular, pero el
    // backend sólo lo acepta con el link de pago ya vencido y anula la COMPRA entera (todos sus
    // holds): el pago de MP es por la compra. Si el link sigue vivo, contesta con el plazo.
    cancel = (item) => {
        const aviso = item.status === 'pending'
            ? 'Esta entrada está esperando un pago online que no llegó. Si el link de pago ya venció, se anulan todas las entradas pendientes de esa compra y se libera el cupo. ¿Continuar?'
            : '¿Anular esta entrada? Se liberará el cupo y no se puede deshacer.';
        if (!window.confirm(aviso)) return;
        const admissionId = item.id;
        this.setState({ disabled: true });
        const request = {
            method: 'PATCH',
            headers: {
                'access-token': localStorage.getItem('token'),
                'Content-Type': 'application/json',
            },
        };
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}manage-events/${this.props.eventId}/admissions/${admissionId}/cancel`, request)
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) {
                    // ENI-116: mensaje de negocio anidado (INVALID_STATE, etc.) si viene.
                    alert((data.data && data.data.message) || data.message);
                    this.setState({ disabled: false });
                    if (data.error == 1002) {
                        window.location.href = localStorage.getItem('homePageURL');
                    }
                } else {
                    this.setState({ disabled: false });
                    this.fetchData({ status: this.state.statusFilter });
                }
            })
            .catch((error) => {
                console.error('No se pudo anular la entrada:', error);
                alert('Error de conexión');
                this.setState({ disabled: false });
            });
    }

    // ENI-162: datos de contacto del asistente. holder_* (nominal) tiene prioridad; si no,
    // se usan los de la cuenta del comprador online (que trae el backend en item.account).
    holderName = (item) => {
        if (item.holder_name) return item.holder_name;
        if (item.account && (item.account.firstName || item.account.lastName)) {
            return `${item.account.firstName || ''} ${item.account.lastName || ''}`.trim();
        }
        if (item.cedula) return item.cedula;
        return '-';
    }

    holderEmail = (item) => item.holder_email || (item.account && item.account.email) || '-';

    // ENI-224: primero el dato de la ENTRADA (lo que se tipeó al vender), después el de la
    // cuenta online. Antes leía sólo la cuenta, así que en una venta de mostrador mostraba "-"
    // (walk-in sin cuenta linkeada) o el teléfono que el cliente tenía cargado en su perfil —
    // un número que el operador nunca escribió. Mismo orden de precedencia que holderEmail.
    holderPhone = (item) => item.holder_phone || (item.account && item.account.phone) || '-';

    // Qué mostrar en la columna Pago. Se decide por el ESTADO y no por el precio: una entrada de
    // $0 es cortesía ("Sin cargo"), y una impaga del mostrador todavía no tiene método elegido.
    paymentCell = (item) => {
        if (item.status === 'unpaid') {
            // Comprada en la web por transferencia: el método ya está elegido, falta ver la plata.
            if (item.payment_method === 'transferencia') return { text: 'Transferencia por confirmar', className: 'text-warning' };
            return { text: 'Pendiente', className: 'text-danger' };
        }
        if (item.status === 'pending') return { text: 'Esperando pago online', className: 'text-warning' };
        if (Number(item.unit_price) === 0 || item.payment_method === 'free') return { text: 'Sin cargo', className: 'text-muted' };
        // Por una 'cancelled' NUNCA entró plata (la paga que se anula va a refund_pending): es un
        // hold de MP vencido o una impaga que no se cobró. Antes caía al caso de abajo y decía
        // "Pagada (Mercado Pago)" sobre una entrada que no se pagó nunca.
        if (item.status === 'cancelled') return { text: 'Sin cobrar', className: 'text-muted' };
        const metodo = PAYMENT_LABEL_BY_METHOD[item.payment_method];
        return { text: metodo ? `Pagada (${metodo})` : 'Pagada', className: 'text-success' };
    }

    // Método con el que se va a cobrar una impaga: lo que eligió el operador; si no, el que
    // declaró el cliente (transferencia online); si no, efectivo.
    cobroMethodFor = (item) => {
        if (this.state.cobroMethod[item.id]) return this.state.cobroMethod[item.id];
        if (COBRO_METHODS.some((m) => m.value === item.payment_method)) return item.payment_method;
        return 'efectivo';
    }

    // Cobrar una entrada que quedó impaga. El backend cobra TODA la venta (si se vendieron 3
    // juntas por teléfono, el cliente paga las 3 de una), por eso la confirmación dice cuántas
    // y por cuánto: el operador tiene que saber qué monto pedir antes de apretar.
    settle = (item) => {
        const elegido = this.cobroMethodFor(item);
        const hermanas = this.state.data.filter(
            (a) => a.status === 'unpaid' && a.purchase_id && a.purchase_id === item.purchase_id,
        );
        const cuantas = Math.max(1, hermanas.length);
        const total = cuantas > 1
            ? hermanas.reduce((acc, a) => acc + Number(a.unit_price || 0), 0)
            : Number(item.unit_price || 0);
        const detalle = cuantas > 1
            ? `Se cobran las ${cuantas} entradas de esta venta`
            : 'Se cobra esta entrada';
        const metodoLabel = (COBRO_METHODS.find((m) => m.value === elegido) || {}).label || elegido;
        if (!window.confirm(`${detalle}: $${total} en ${metodoLabel}. ¿Confirmás?`)) return;
        this.setState({ disabled: true });
        const request = {
            method: 'PATCH',
            headers: {
                'access-token': localStorage.getItem('token'),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ paymentMethod: elegido }),
        };
        fetch(`${config.REACT_APP_BASE_URL}${config.REACT_APP_VERSION}manage-events/${this.props.eventId}/admissions/${item.id}/pay`, request)
            .then((results) => results.json())
            .then((data) => {
                if (data.error > 0) {
                    // ENI-116: mensaje de negocio anidado (INVALID_STATE, etc.) si viene.
                    alert((data.data && data.data.message) || data.message);
                    this.setState({ disabled: false });
                    if (data.error == 1002) {
                        window.location.href = localStorage.getItem('homePageURL');
                    }
                } else {
                    this.setState({ disabled: false });
                    this.fetchData({ status: this.state.statusFilter });
                }
            })
            .catch((error) => {
                console.error('No se pudo registrar el pago:', error);
                alert('Error de conexión');
                this.setState({ disabled: false });
            });
    }

    // ── La mesa de cada persona (jornadas del club) ──
    // Las mesas del plan salen de la actividad de la jornada (la misma que usa la card de mesas).
    // Se sortean y se reasignan las entradas VIVAS que todavía no ingresaron; la que ya entró
    // está sentada y se mueve desde el salón.
    mesas = () => {
        const act = (this.props || {}).activity;
        return act && act.status && act.status !== 'done' && act.status !== 'cancelled' ? (act.plannedTables || []) : [];
    }

    labelMesa = (tableId) => {
        const act = (this.props || {}).activity;
        const m = ((act && act.plannedTables) || []).find((x) => Number(x.tableId) === Number(tableId));
        return m ? m.label : null;
    }

    sortear = async () => {
        const act = this.props.activity;
        const hayAsignadas = this.state.data.some((a) => SORTEABLES.includes(a.status) && a.assigned_table_id);
        if (hayAsignadas && !window.confirm('Volver a sortear reparte de nuevo, al azar, a todos los que todavía no entraron. Los cambios a mano se pierden. ¿Seguro?')) return;
        this.setState({ disabled: true, mesasAviso: null });
        try {
            const r = await drawTables(act.id);
            const reparto = (r.tables || []).map((t) => `${t.label}: ${t.count}`).join(' · ');
            const avisos = (r.warnings || []).map((w) => `${w.label}: ${w.reason}`).join(' · ');
            this.setState({ disabled: false, mesasAviso: { ok: !avisos, text: `Mesas sorteadas (${r.drawn} ${r.drawn === 1 ? 'persona' : 'personas'}) — ${reparto}${avisos ? `. Ojo, no entran todos: ${avisos}` : ''}` } });
            this.fetchData({ status: this.state.statusFilter });
        } catch (e) {
            this.setState({ disabled: false });
            alert(e.message);
        }
    }

    asignar = async (item, tableId) => {
        this.setState({ disabled: true });
        try {
            await assignTable(this.props.activity.id, item.id, tableId);
            this.setState({ disabled: false });
            this.fetchData({ status: this.state.statusFilter });
        } catch (e) {
            this.setState({ disabled: false });
            alert(e.message);
        }
    }

    // El reparto como texto para pegar en WhatsApp: una mesa por bloque, un nombre por línea.
    textoMesas = () => {
        const mesas = this.mesas();
        // Sólo los nombres. Una entrada vendida "sin datos" no tiene: sale "Sin nombre", para que
        // se cuente igual.
        const nombre = (a) => {
            const n = this.holderName(a);
            return n && n !== '-' ? n : 'Sin nombre';
        };
        const vivas = this.state.data.filter((a) => SORTEABLES.includes(a.status) || a.status === 'redeemed');
        const bloques = mesas.map((m) => {
            const nombres = vivas.filter((a) => Number(a.assigned_table_id) === Number(m.tableId)).map(nombre);
            return `${m.label}:\n${nombres.length ? nombres.join('\n') : '(nadie todavía)'}`;
        });
        const sinMesa = vivas.filter((a) => !mesas.some((m) => Number(m.tableId) === Number(a.assigned_table_id))).map(nombre);
        if (sinMesa.length) bloques.push(`Sin mesa:\n${sinMesa.join('\n')}`);
        return bloques.join('\n\n');
    }

    copiarMesas = () => {
        const texto = this.textoMesas();
        // copyText y no navigator.clipboard: en la red del local el backoffice va por http y la
        // Clipboard API no existe. Si no copia, el texto queda abajo para copiarlo a mano.
        this.setState({ mesasTexto: texto, mesasCopiado: copyText(texto) });
    }

    renderMesas() {
        const mesas = this.mesas();
        if (!mesas.length) return null;
        const vivas = this.state.data.filter((a) => SORTEABLES.includes(a.status) || a.status === 'redeemed');
        const cuenta = (tableId) => vivas.filter((a) => Number(a.assigned_table_id) === Number(tableId)).length;
        const sinMesa = vivas.filter((a) => !a.assigned_table_id || !mesas.some((m) => Number(m.tableId) === Number(a.assigned_table_id))).length;
        const hayAsignadas = vivas.some((a) => a.assigned_table_id);
        const completa = this.state.statusFilter === 'all';
        return (
            <div className="px-3 py-2 border-bottom">
                <div className="d-flex flex-wrap align-items-center" style={{ gap: 8, fontSize: 13 }}>
                    <b>Mesas de la jornada</b>
                    {completa ? (
                        <>
                            {mesas.map((m) => (
                                <span key={m.tableId} className={`badge ${m.capacity != null && cuenta(m.tableId) > m.capacity ? 'badge-warning' : 'badge-light'} border`} style={{ fontSize: 12 }}>
                                    {m.label}: {cuenta(m.tableId)}{m.capacity != null ? `/${m.capacity}` : ''}
                                </span>
                            ))}
                            <span className={`badge ${sinMesa ? 'badge-info' : 'badge-light border'}`} style={{ fontSize: 12 }}>Sin mesa: {sinMesa}</span>
                        </>
                    ) : (
                        <span className="text-muted">poné el filtro en "Todos" para ver cuántos hay por mesa</span>
                    )}
                    <button type="button" className="btn btn-sm btn-white ml-auto" disabled={this.state.disabled || !this.state.loaded} onClick={this.sortear}>
                        {hayAsignadas ? 'Volver a sortear' : 'Sortear mesas'}
                    </button>
                    {/* Sólo con el filtro en "Todos": con otro filtro la lista está incompleta y el
                        texto dejaría gente afuera sin avisar. */}
                    <button type="button" className="btn btn-sm btn-white" disabled={!completa || !hayAsignadas || !this.state.loaded}
                        title={completa ? 'Copia el reparto para pegarlo en WhatsApp' : 'Poné el filtro en "Todos"'}
                        onClick={this.copiarMesas}>
                        Copiar para WhatsApp
                    </button>
                </div>
                {this.state.mesasTexto ? (
                    <div className="mt-2" style={{ fontSize: 12.5 }}>
                        <div className="d-flex align-items-center mb-1" style={{ gap: 8 }}>
                            <span className={this.state.mesasCopiado ? 'text-success' : 'text-muted'}>
                                {this.state.mesasCopiado ? 'Copiado: pegalo en WhatsApp.' : 'Seleccioná el texto y copialo:'}
                            </span>
                            <button type="button" className="btn btn-sm btn-link p-0" onClick={() => this.setState({ mesasTexto: null })}>Cerrar</button>
                        </div>
                        <textarea className="form-control" readOnly rows={Math.min(14, this.state.mesasTexto.split('\n').length + 1)}
                            value={this.state.mesasTexto} onFocus={(e) => e.target.select()} style={{ fontSize: 12.5, maxWidth: 420 }} />
                    </div>
                ) : null}
                {this.state.mesasAviso ? (
                    <div className={`mt-2 mb-0 py-1 px-2 alert ${this.state.mesasAviso.ok ? 'alert-success' : 'alert-warning'}`} style={{ fontSize: 12.5 }}>
                        {this.state.mesasAviso.text}
                    </div>
                ) : null}
            </div>
        );
    }

    renderCeldaMesa(item) {
        const mesas = this.mesas();
        if (item.status === 'redeemed') return this.labelMesa(item.assigned_table_id) || '-';
        if (!SORTEABLES.includes(item.status)) return '-';
        return (
            <select className="custom-select custom-select-sm" style={{ width: 'auto' }} disabled={this.state.disabled}
                value={item.assigned_table_id && mesas.some((m) => Number(m.tableId) === Number(item.assigned_table_id)) ? String(item.assigned_table_id) : ''}
                onChange={(e) => this.asignar(item, e.target.value)}>
                <option value="">Sin mesa</option>
                {mesas.map((m) => <option key={m.tableId} value={String(m.tableId)}>{m.label}</option>)}
            </select>
        );
    }

    // Un backend viejo no manda el campo: `!== false` deja el botón como siempre.
    puedeMarcarIngreso = () => !(this.state.event && this.state.event.canRedeemToday === false)

    statusLabel = () => {
        const estado = (STATUS_FILTER_OPTIONS.find((o) => o.value === this.state.statusFilter) || {}).label
            || this.state.statusFilter;
        // La búsqueda también recorta lo que se imprime, así que tiene que figurar en el papel:
        // un listado que dice sólo "Filtro: Todos" y trae tres filas se lee como si al evento
        // hubieran venido tres personas.
        const buscado = this.state.search.trim();
        return buscado ? `${estado} · búsqueda "${buscado}"` : estado;
    }

    // Imprime el listado tal como está en pantalla — el mismo filtro y los mismos textos por
    // fila, con las columnas que se tildaron en el modal. El backend no pagina las admisiones,
    // así que lo que se ve ES la lista entera del filtro elegido y no hace falta volver a
    // pedirla para imprimirla completa.
    print = (columns) => {
        const event = this.state.event || {};
        printAdmissionsList({
            columns,
            eventTitle: event.title || '',
            eventDate: event.starts_at ? formatEventDateTime(event.starts_at) : '',
            statusLabel: this.statusLabel(),
            // Con mesas en la jornada, la hoja sale agrupada por mesa: es el listado para que cada
            // mesa vote su juego. Los "sin mesa" van al final. Sale de `visibleAdmissions()` y no
            // de todo el evento: si hay una búsqueda puesta, el papel es lo que está en pantalla.
            rows: [...this.visibleAdmissions()].sort((a, b) => {
                if (!this.mesas().length) return 0;
                const la = this.labelMesa(a.assigned_table_id); const lb = this.labelMesa(b.assigned_table_id);
                if (!la && !lb) return 0; if (!la) return 1; if (!lb) return -1;
                return la.localeCompare(lb, 'es', { numeric: true });
            }).map((item) => ({
                name: this.holderName(item),
                table: this.labelMesa(item.assigned_table_id) || '-',
                email: this.holderEmail(item),
                phone: this.holderPhone(item),
                ticketType: item.ticketType ? item.ticketType.name : '-',
                date: item.created_at ? dateFormat(item.created_at, 'dd/mm/yyyy HH:MM') : '-',
                payment: this.paymentCell(item).text,
                status: ADMISSION_STATUS_LABEL[item.status] || item.status,
            })),
        });
    }

    render() {
        const visibles = this.visibleAdmissions();

        return (
            <div className="card card-small mb-4">
                <div className="card-body p-0">
                    <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                        <label className="mb-0">Estado:&nbsp;
                            <select className="custom-select" style={{ width: 'auto', display: 'inline-block' }} value={this.state.statusFilter} onChange={this.onChangeStatus}>
                                {STATUS_FILTER_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </label>
                        {/* El buscador estaba faltando: en un evento con cien entradas, encontrar
                            a la persona que está en la puerta era recorrer la grilla a ojo. */}
                        <label className="mb-0 ml-3">Buscar:&nbsp;
                            <input
                                type="search"
                                className="form-control"
                                style={{ width: '260px', display: 'inline-block' }}
                                placeholder="Nombre, mail, celular…"
                                value={this.state.search}
                                onChange={this.onChangeSearch}
                            />
                        </label>
                        {/* Abre el checklist de columnas; imprime recién al confirmar ahí.
                            Deshabilitado mientras carga o si no hay filas: un papel en blanco
                            con el encabezado del evento se confunde con "no vino nadie". */}
                        <button type="button" className="btn btn-white text-nowrap"
                            disabled={!this.state.loaded || visibles.length === 0}
                            data-toggle="modal" data-target="#modal-print-admissions">
                            <i className="material-icons" style={{ fontSize: '16px', verticalAlign: 'middle' }}>print</i>&nbsp;Imprimir listado
                        </button>
                    </div>
                    {this.renderActivityNotice()}
                    {!this.puedeMarcarIngreso() && this.state.event.redeemBlockReason ? (
                        <div className="px-3 py-2 border-bottom text-muted" style={{ fontSize: 13 }}>
                            {this.state.event.redeemBlockReason}
                        </div>
                    ) : null}
                    {this.renderSentarATodos()}
                    {this.renderMesas()}
                    <table className="table mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th>Nombre</th>
                                <th>Mail</th>
                                <th>Celular</th>
                                <th>Tipo de entrada</th>
                                {this.mesas().length ? <th>Mesa</th> : null}
                                <th>Fecha</th>
                                <th>Pago</th>
                                <th>Estado</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {!this.state.loaded && (
                                <tr><td colSpan="9" className="text-center">Cargando…</td></tr>
                            )}
                            {this.state.loaded && this.state.data.length === 0 && (
                                <tr><td colSpan="9" className="text-center text-muted">No hay asistentes para mostrar.</td></tr>
                            )}
                            {/* Vacío POR LA BÚSQUEDA es distinto de vacío a secas: si dice lo
                                mismo, el operador cree que la entrada no existe en vez de que
                                no matcheó. */}
                            {this.state.loaded && this.state.data.length > 0 && visibles.length === 0 && (
                                <tr><td colSpan="9" className="text-center text-muted">
                                    Ningún asistente coincide con "{this.state.search.trim()}".
                                </td></tr>
                            )}
                            {this.state.loaded && visibles.map((item) => (
                                // La fila recién marcada se resalta un momento: es la
                                // confirmación de que el ingreso quedó.
                                <tr key={item.id} style={item.id === this.state.recienIngreso
                                    ? { background: '#e6f6ec', transition: 'background .6s' } : { transition: 'background .6s' }}>
                                    <td>{this.holderName(item)}</td>
                                    <td>{this.holderEmail(item)}</td>
                                    <td>{this.holderPhone(item)}</td>
                                    <td>{item.ticketType ? item.ticketType.name : '-'}</td>
                                    {this.mesas().length ? <td>{this.renderCeldaMesa(item)}</td> : null}
                                    <td>{item.created_at ? dateFormat(item.created_at, 'dd/mm/yyyy HH:MM') : '-'}</td>
                                    <td className={this.paymentCell(item).className}>{this.paymentCell(item).text}</td>
                                    <td>{ADMISSION_STATUS_LABEL[item.status] || item.status}</td>
                                    <td className="text-right">
                                        {/* Impaga: primero se cobra. El check-in no aparece hasta
                                            entonces — el backend lo rechaza igual, y ofrecerlo
                                            sería invitar a dejar entrar a alguien que no pagó. */}
                                        {item.status === 'unpaid' && (
                                            <select className="custom-select custom-select-sm mr-2" style={{ width: 'auto', display: 'inline-block' }}
                                                value={this.cobroMethodFor(item)}
                                                onChange={(e) => this.setState({ cobroMethod: { ...this.state.cobroMethod, [item.id]: e.target.value } })}>
                                                {COBRO_METHODS.map((m) => (
                                                    <option key={m.value} value={m.value}>{m.label}</option>
                                                ))}
                                            </select>
                                        )}
                                        {item.status === 'unpaid' && (
                                            <button type="button" className="btn btn-sm btn-accent" disabled={this.state.disabled} onClick={() => this.settle(item)}>
                                                Registrar pago
                                            </button>
                                        )}
                                        {/* Sólo el día del evento (lo decide el backend: `canRedeemToday`);
                                            fuera de día, el motivo va una vez arriba de la lista. */}
                                        {item.status === 'valid' && this.puedeMarcarIngreso() && (
                                            <button type="button" className="btn btn-sm btn-accent" disabled={this.state.disabled} onClick={() => this.marcarIngreso(item.id)}>
                                                Marcar ingreso
                                            </button>
                                        )}
                                        {(item.status === 'valid' || item.status === 'unpaid' || item.status === 'pending') && (
                                            <button type="button" className="btn btn-sm btn-outline-danger ml-2" disabled={this.state.disabled} onClick={() => this.cancel(item)}>
                                                Anular
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <ModalPrintAdmissionsComponent
                    rowCount={this.state.data.length}
                    statusLabel={this.statusLabel()}
                    onPrint={this.print}
                />
            </div>
        );
    }
}

export default TableEventAdmissionsComponent;
